import React, { useState } from "react";
import { Box, Typography, Grid, Tooltip, Chip } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Message as MessageIcon,
  Phone,
  DirectionsCar,
  TwoWheeler,
  Star,
  SentimentVeryDissatisfied,
  SentimentDissatisfied,
  SentimentNeutral,
  SentimentSatisfied,
  SentimentVerySatisfied,
  Feedback as FeedbackIcon,
  RateReview,
  TrendingUp,
  ChatBubble,
} from "@mui/icons-material";
import { formatDistanceToNow, format } from "date-fns";
import { StatCard } from "../../components/common";
import FeedbackRatingCard from "../../components/FeedbackRatingCard";
import ScrollToTopFab from "../../components/ScrollToTopFab";
import HeroSection from "../../components/HeroSection";
import { TableColumn } from "../../components/DataTable";
import DataTableCard from "../../components/DataTableCard";
import PageToolbar from "../../components/Toolbar/PageToolbar";
import { useGetFeedbackDetailsQuery } from "../../features/messages/messagesApiSlice";
import { useGetDailyAnalyticsQuery } from "../../features/analytics/analyticsApiSlice";
import { useAnalyticsDateParams } from "../../utils/dateFilterUtils";
import { useAppSelector } from "../../store/store";
import { useCacheValidation, useErrorToast } from "../../hooks";
import { ICON_SIZE } from "../../utils/themeConstants";

const Messages: React.FC = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  // Use scroll-to-top hook for button visibility and scroll functionality

  // Use utility hook to get date params (handles both single and range modes correctly)
  const dateParams = useAnalyticsDateParams();

  // venueId is extracted from JWT token by backend automatically

  // Check if we have cached data from Dashboard using custom hook
  const cachedDailyData = useAppSelector((state) => state.analytics.dailyCache);
  const isCacheValid = useCacheValidation({
    cache: cachedDailyData,
    startDate: dateParams.startDate,
    endDate: dateParams.endDate,
    cacheTTL: 60000, // 1 minute cache
  });

  // Get feedback metrics from analytics API (for summary cards)
  // Skip API call if we have valid cached data from Dashboard
  const {
    data: analyticsResponse,
    isLoading: isLoadingAnalytics,
    isFetching: isFetchingAnalytics,
    isError: isAnalyticsError,
  } = useGetDailyAnalyticsQuery(
    {
      ...dateParams,
    },
    { skip: isCacheValid },
  );

  // Get feedback details from feedback API (for table)
  const {
    data: feedbackResponse,
    isLoading: isLoadingFeedback,
    isFetching: isFetchingFeedback,
    isError: isFeedbackError,
  } = useGetFeedbackDetailsQuery({
    startDate: dateParams.startDate,
    endDate: dateParams.endDate,
    page,
    limit,
  });

  // Get current error (only show if query is active, not skipped)
  const currentIsError = (!isCacheValid && isAnalyticsError) || isFeedbackError;

  // Extract feedbackMetrics from cached data or API response
  const analyticsData = isCacheValid
    ? { data: cachedDailyData.data }
    : analyticsResponse;
  const feedbackMetrics = analyticsData?.data?.[0]?.feedbackMetrics || {
    totalMallFeedback: 0,
    totalParkingFeedback: 0,
    averageMallRating: 0,
    averageParkingRating: 0,
    mallRatingBreakdown: {
      excellent: 0,
      good: 0,
      average: 0,
      poor: 0,
      terrible: 0,
    },
    parkingRatingBreakdown: {
      excellent: 0,
      good: 0,
      average: 0,
      poor: 0,
      terrible: 0,
    },
  };

  // Extract feedback details for table
  const feedbackDetails = feedbackResponse?.data || [];
  const pagination = feedbackResponse?.pagination || {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  };

  // Helper to parse rating from experience string (e.g., "Excellent (5/5)" -> 5)
  const parseRating = (experience: string): number => {
    const match = experience.match(/\((\d+)\/5\)/);
    return match ? parseInt(match[1]) : 0;
  };

  const getParkingExperienceIcon = (experience: string) => {
    const rating = parseRating(experience);
    switch (rating) {
      case 5:
        return (
          <SentimentVerySatisfied
            sx={{ fontSize: ICON_SIZE.SM, color: "#4caf50" }}
          />
        );
      case 4:
        return (
          <SentimentSatisfied
            sx={{ fontSize: ICON_SIZE.SM, color: "#2196f3" }}
          />
        );
      case 3:
        return (
          <SentimentNeutral sx={{ fontSize: ICON_SIZE.SM, color: "#ff9800" }} />
        );
      case 2:
        return (
          <SentimentDissatisfied
            sx={{ fontSize: ICON_SIZE.SM, color: "#f44336" }}
          />
        );
      default:
        return (
          <SentimentVeryDissatisfied
            sx={{ fontSize: ICON_SIZE.SM, color: "#d32f2f" }}
          />
        );
    }
  };

  const getVehicleIcon = (vehicleType: string) => {
    return vehicleType.toLowerCase() === "bike" ? (
      <TwoWheeler sx={{ color: "#1a237e", fontSize: ICON_SIZE.XS }} />
    ) : (
      <DirectionsCar sx={{ color: "#3949ab", fontSize: ICON_SIZE.XS }} />
    );
  };

  // Handle error toast notifications with automatic deduplication and mount delay
  useErrorToast({
    isError: currentIsError,
    isFetching: isFetchingAnalytics || isFetchingFeedback,
    isLoading: isLoadingAnalytics || isLoadingFeedback,
    errorMessage: "Failed to load feedback data. Please try again.",
  });

  // Get breakdown data for positive feedback calculation
  const totalParkingFeedback = feedbackMetrics.totalParkingFeedback || 1;
  const parkingBreakdown = feedbackMetrics.parkingRatingBreakdown;
  const totalMallFeedback = feedbackMetrics.totalMallFeedback || 1;
  const mallBreakdown = feedbackMetrics.mallRatingBreakdown;

  // Table columns definition
  const feedbackTableColumns: TableColumn<any>[] = [
    {
      key: "customer",
      label: "Customer & Vehicle No",
      render: (_, feedback) => (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Phone sx={{ fontSize: ICON_SIZE.XS, color: "text.secondary" }} />
            <Typography variant="body2" fontWeight={600}>
              {feedback.customerMobile}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {getVehicleIcon(feedback.vehicleType)}
            <Typography variant="caption" color="text.secondary">
              {feedback.vehicleNumber || "N/A"}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      key: "mallExperience",
      label: "Mall Experience",
      render: (_, feedback) => (
        <Chip
          icon={getParkingExperienceIcon(feedback.mallExperience)}
          label={feedback.mallExperience}
          size="small"
          variant="outlined"
          sx={{
            fontWeight: 600,
            borderColor:
              parseRating(feedback.mallExperience) === 5
                ? "#4caf50"
                : parseRating(feedback.mallExperience) === 4
                  ? "#2196f3"
                  : parseRating(feedback.mallExperience) === 3
                    ? "#ff9800"
                    : "#f44336",
            color:
              parseRating(feedback.mallExperience) === 5
                ? "#4caf50"
                : parseRating(feedback.mallExperience) === 4
                  ? "#2196f3"
                  : parseRating(feedback.mallExperience) === 3
                    ? "#ff9800"
                    : "#f44336",
          }}
        />
      ),
    },
    {
      key: "parkingExperience",
      label: "Parking Experience",
      render: (_, feedback) => (
        <Chip
          icon={getParkingExperienceIcon(feedback.parkingExperience)}
          label={feedback.parkingExperience}
          size="small"
          variant="filled"
          sx={{
            fontWeight: 600,
            background:
              parseRating(feedback.parkingExperience) === 5
                ? "linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)"
                : parseRating(feedback.parkingExperience) === 4
                  ? "linear-gradient(45deg, #2196f3 30%, #42a5f5 90%)"
                  : parseRating(feedback.parkingExperience) === 3
                    ? "linear-gradient(45deg, #ff9800 30%, #ffb74d 90%)"
                    : "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
            color: "white",
            "& .MuiChip-icon": {
              color: "white !important",
            },
          }}
        />
      ),
    },
    {
      key: "message",
      label: "Feedback Message",
      render: (_, feedback) => (
        <Box sx={{ maxWidth: 350 }}>
          {feedback.message && feedback.message.trim() ? (
            <Tooltip title={feedback.message} placement="top">
              <Typography
                variant="body2"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  lineHeight: 1.4,
                  fontStyle: "italic",
                  color: "text.primary",
                }}
              >
                "{feedback.message}"
              </Typography>
            </Tooltip>
          ) : (
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ fontStyle: "italic" }}
            >
              No message provided
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "submittedAt",
      label: "Submitted",
      render: (_, feedback) => (
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="body2" fontWeight={500}>
            {format(new Date(feedback.submittedAt), "MMM dd, yyyy")}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#1a237e" }}
            fontWeight={500}
          >
            {format(new Date(feedback.submittedAt), "HH:mm")}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 0.5 }}
          >
            {formatDistanceToNow(new Date(feedback.submittedAt), {
              addSuffix: true,
            })}
          </Typography>
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageToolbar showDateFilter />

      {/* Hero Section with Framer Motion */}
      <HeroSection
        title="Customer Feedback Hub"
        subtitle="Real-time visitor satisfaction ratings for mall shopping and parking experiences"
        gradient="navyLux"
      />

      <Box sx={{ py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 2, md: 1.5 } }}>
        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key="messages-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Section Header: Feedback Overview */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      background:
                        "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                      boxShadow: "0 6px 16px rgba(26, 35, 126, 0.3)",
                    }}
                  >
                    <Star sx={{ fontSize: 24, color: "white" }} />
                  </Box>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: "text.primary",
                        letterSpacing: "-0.5px",
                        fontSize: { xs: "1.2rem", md: "1.4rem" },
                        mb: 0.5,
                      }}
                    >
                      Satisfaction Metrics
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.85rem",
                        fontWeight: 500,
                      }}
                    >
                      Real-time visitor ratings and feedback response analytics
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Summary Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Total Feedback"
                      value={feedbackMetrics.totalParkingFeedback}
                      subtitle={(() => {
                        const totalTickets =
                          analyticsData?.data?.[0]?.totalTickets || 0;
                        const totalFeedback =
                          feedbackMetrics.totalParkingFeedback;
                        const percentage =
                          totalTickets > 0
                            ? Math.round((totalFeedback / totalTickets) * 100)
                            : 0;
                        return `Out of ${totalTickets} tickets (${percentage}%)`;
                      })()}
                      icon={<FeedbackIcon />}
                      gradient="royalBlue"
                      isLoading={isLoadingAnalytics || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Avg Mall Rating"
                      value={feedbackMetrics.averageMallRating.toFixed(1)}
                      subtitle="Mall experience rating"
                      icon={<RateReview />}
                      gradient="gold"
                      isLoading={isLoadingAnalytics || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Avg Parking Rating"
                      value={feedbackMetrics.averageParkingRating.toFixed(1)}
                      subtitle="Out of 5.0 stars"
                      icon={<Star />}
                      gradient="emerald"
                      isLoading={isLoadingAnalytics || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                    style={{ height: "100%" }}
                  >
                    <StatCard
                      label="Positive Feedback"
                      value={`${Math.round(
                        ((parkingBreakdown.excellent +
                          parkingBreakdown.good +
                          mallBreakdown.excellent +
                          mallBreakdown.good) /
                          (totalParkingFeedback + totalMallFeedback)) *
                          100,
                      )}%`}
                      subtitle="Mall & Parking (Excellent + Good)"
                      icon={<TrendingUp />}
                      gradient="deepPurple"
                      isLoading={isLoadingAnalytics || isFetchingAnalytics}
                    />
                  </motion.div>
                </Grid>
              </Grid>

              {/* Satisfaction Breakdown - Mall & Parking */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Mall Rating */}
                <Grid item xs={12} md={6}>
                  <FeedbackRatingCard
                    title="Mall Experience"
                    icon={Star}
                    iconGradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                    iconShadow="0 4px 16px rgba(250,112,154,0.35)"
                    totalFeedback={feedbackMetrics.totalMallFeedback}
                    totalTickets={analyticsData?.data?.[0]?.totalTickets || 0}
                    averageRating={feedbackMetrics.averageMallRating}
                    ratingBreakdown={feedbackMetrics.mallRatingBreakdown}
                    chipBgColor="rgba(250,112,154,0.12)"
                    chipTextColor="#fa709a"
                    chipBorderColor="rgba(250,112,154,0.25)"
                    ratingGradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)"
                    ratingBoxGradient="linear-gradient(135deg, rgba(250,112,154,0.1) 0%, rgba(254,225,64,0.12) 100%)"
                    ratingBoxBorderColor="rgba(250,112,154,0.25)"
                    hoverBorderColor="rgba(26,35,126,0.2)"
                    isLoading={isLoadingAnalytics || isFetchingAnalytics}
                  />
                </Grid>

                {/* Parking Rating */}
                <Grid item xs={12} md={6}>
                  <FeedbackRatingCard
                    title="Parking Experience"
                    icon={DirectionsCar}
                    iconGradient="linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
                    iconShadow="0 4px 8px rgba(57,73,171,0.3)"
                    totalFeedback={feedbackMetrics.totalParkingFeedback}
                    totalTickets={analyticsData?.data?.[0]?.totalTickets || 0}
                    averageRating={feedbackMetrics.averageParkingRating}
                    ratingBreakdown={feedbackMetrics.parkingRatingBreakdown}
                    chipBgColor="rgba(26,35,126,0.12)"
                    chipTextColor="#1a237e"
                    chipBorderColor="rgba(26,35,126,0.25)"
                    ratingGradient="linear-gradient(135deg, #1a237e 0%, #3949ab 100%)"
                    ratingBoxGradient="linear-gradient(135deg, rgba(26,35,126,0.1) 0%, rgba(57,73,171,0.12) 100%)"
                    ratingBoxBorderColor="rgba(26,35,126,0.25)"
                    hoverBorderColor="rgba(26,35,126,0.2)"
                    isLoading={isLoadingAnalytics || isFetchingAnalytics}
                  />
                </Grid>
              </Grid>

              {/* Feedback Table */}
              <DataTableCard
                title="Detailed Feedback Messages"
                subtitle="Individual customer responses with ratings and comments"
                icon={ChatBubble}
                iconSize={28}
                iconColor="#1a237e"
                data={feedbackDetails}
                columns={feedbackTableColumns}
                countChip={{
                  label: `${pagination.total} Total`,
                  color: "primary",
                  variant: "outlined",
                }}
                headerBackground="linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)"
                emptyState={{
                  icon: (
                    <MessageIcon
                      sx={{
                        fontSize: 64,
                        color: "primary.main",
                        opacity: 0.5,
                      }}
                    />
                  ),
                  title: "No feedback messages found",
                  subtitle:
                    "Customer feedback will appear here when they submit responses",
                }}
                isLoading={isLoadingFeedback}
                isFetching={isFetchingFeedback}
                enableSearch
                searchPlaceholder="Search by mobile, vehicle type, or message..."
                searchableColumns={["customerMobile", "vehicleType", "message"]}
                showTableOnEmpty
                pagination={{
                  page,
                  limit,
                  total: pagination.total,
                  onPageChange: setPage,
                  onLimitChange: setLimit,
                }}
                skeletonConfig={{
                  rows: 5,
                  textColumns: 2,
                  chipColumns: 2,
                  showIconHeader: true,
                }}
              />
            </Box>
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Scroll to Top Button */}
      <ScrollToTopFab />
    </>
  );
};

/**
 * Memoized Messages component for optimal performance.
 * Prevents unnecessary re-renders when parent components update.
 */
export default React.memo(Messages);
